import { Link } from 'react-router-dom';
import {
  ShieldCheckIcon,
  CalendarDaysIcon,
  DocumentTextIcon,
  CreditCardIcon,
  ChatBubbleLeftRightIcon,
  ChartBarIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  HeartIcon,
} from '@heroicons/react/24/outline';
import LandingLayout from '../components/layout/LandingLayout';

const features = [
  {
    icon: CalendarDaysIcon,
    title: 'Smart scheduling',
    desc: 'Online booking, automated reminders, and calendar sync — so clients show up and you stay organized.',
  },
  {
    icon: DocumentTextIcon,
    title: 'Clinical notes & treatment plans',
    desc: 'Structured, HIPAA-compliant documentation with customizable templates and e-signatures.',
  },
  {
    icon: CreditCardIcon,
    title: 'Billing & insurance',
    desc: 'Superbills, ERA processing, and real-time eligibility checks. Get paid without the paperwork maze.',
  },
  {
    icon: ChatBubbleLeftRightIcon,
    title: 'Secure messaging',
    desc: 'HIPAA-compliant messaging between providers and clients — no unsecured email.',
  },
  {
    icon: ShieldCheckIcon,
    title: 'Built-in HIPAA compliance',
    desc: 'Audit logs, BAA agreements, and encrypted storage — compliance built in, not bolted on.',
  },
  {
    icon: ChartBarIcon,
    title: 'Practice analytics',
    desc: 'Revenue reports, caseload insights, and appointment utilization in one dashboard.',
  },
];

const commitments = [
  { value: '30-day', label: 'free trial, no card needed' },
  { value: 'HIPAA', label: 'compliant from day one' },
  { value: 'BAA', label: 'included at no extra cost' },
  { value: 'Cancel', label: 'anytime, no questions asked' },
];

const earlyAccessBenefits = [
  'Free 30-day trial, no credit card required',
  'Direct line to the product team — your feedback shapes the roadmap',
  'Help us build features that actually matter to your practice',
  'Founding member pricing locked in when we launch',
];

export default function Landing() {
  return (
    <LandingLayout>
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary-950 via-primary-900 to-primary-800 text-white">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTM2IDM0djZoNnYtNmgtNnptNiA2djZoNnYtNmgtNnptLTEyIDBoNnY2aC02di02em0xMiAwaDZ2Nmg2di02aC02eiIvPjwvZz48L2c+PC9zdmc+')] opacity-40" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur rounded-full px-4 py-1.5 text-sm font-medium text-primary-100 mb-6 border border-white/10">
              <ShieldCheckIcon className="w-4 h-4" />
              HIPAA-compliant from day one
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight leading-tight mb-6">
              Modern practice management for{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-300 to-cyan-300">
                mental health
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-white/80 leading-relaxed mb-10 max-w-2xl">
              Sage gives therapists, psychologists, and counselors the tools to run a thriving practice —
              scheduling, notes, billing, and client communication in one HIPAA-secure platform.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/register"
                className="inline-flex items-center justify-center gap-2 bg-white text-primary-700 font-semibold px-6 py-3.5 rounded-xl hover:bg-primary-50 transition-colors shadow-lg"
              >
                Start free trial
                <ArrowRightIcon className="w-4 h-4" />
              </Link>
              <Link
                to="/for-providers"
                className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur text-white font-semibold px-6 py-3.5 rounded-xl border border-white/20 transition-colors"
              >
                I&apos;m a provider
              </Link>
              <Link
                to="/for-clients"
                className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur text-white font-semibold px-6 py-3.5 rounded-xl border border-white/20 transition-colors"
              >
                I&apos;m a client
              </Link>
            </div>
            <p className="mt-4 text-sm text-white/60">No credit card required · Free 30-day trial · Cancel anytime</p>
          </div>
        </div>

        {/* Decorative card mockup */}
        <div className="hidden lg:block absolute right-0 top-1/2 -translate-y-1/2 w-[480px] pr-8">
          <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-5 shadow-2xl space-y-3">
            <div className="flex items-center gap-3 border-b border-white/10 pb-3">
              <div className="w-8 h-8 rounded-full bg-primary-400/40 flex items-center justify-center text-xs font-semibold text-white">AC</div>
              <div>
                <p className="text-sm font-medium text-white">Alex Chen — Today 2:00 PM</p>
                <p className="text-xs text-white/70">Session note ready to sign</p>
              </div>
              <span className="ml-auto text-xs bg-success-500/20 text-success-300 px-2 py-0.5 rounded-full font-medium">Upcoming</span>
            </div>
            <div className="flex items-center gap-3 border-b border-white/10 pb-3">
              <div className="w-8 h-8 rounded-full bg-cyan-400/30 flex items-center justify-center text-xs font-semibold text-white">MJ</div>
              <div>
                <p className="text-sm font-medium text-white">Marcus Johnson — 4:30 PM</p>
                <p className="text-xs text-white/70">Intake form completed</p>
              </div>
              <span className="ml-auto text-xs bg-primary-400/20 text-primary-300 px-2 py-0.5 rounded-full font-medium">New</span>
            </div>
            <div className="bg-white/5 rounded-xl p-3">
              <p className="text-xs text-cyan-300 mb-1">This week</p>
              <div className="flex items-end gap-1 h-12">
                {[60, 80, 45, 90, 70, 95, 55].map((h, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={i} className="flex-1 bg-primary-400/40 rounded-sm" style={{ height: `${h}%` }} />
                ))}
              </div>
              <div className="flex justify-between mt-1 text-xs text-white/50">
                <span>M</span><span>T</span><span>W</span><span>T</span><span>F</span><span>S</span><span>S</span>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="flex-1 bg-white/5 rounded-xl p-3">
                <p className="text-xs text-cyan-300">Revenue</p>
                <p className="text-lg font-bold text-white mt-0.5">$8,240</p>
                <p className="text-xs text-success-300">↑ 12% this month</p>
              </div>
              <div className="flex-1 bg-white/5 rounded-xl p-3">
                <p className="text-xs text-cyan-300">Appointments</p>
                <p className="text-lg font-bold text-white mt-0.5">47</p>
                <p className="text-xs text-success-300">↑ 3 vs last week</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Launch commitments */}
      <section className="bg-neutral-950 text-white py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {commitments.map(({ value, label }) => (
              <div key={label}>
                <p className="text-3xl sm:text-4xl font-extrabold text-primary-400">{value}</p>
                <p className="text-sm text-neutral-400 mt-1">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-sm font-semibold text-primary-600 uppercase tracking-widest mb-3">Everything you need</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-neutral-900 tracking-tight">
              One platform. Zero compromises.
            </h2>
            <p className="mt-4 text-lg text-neutral-500 max-w-2xl mx-auto">
              From first appointment to final invoice, Sage handles every part of your practice workflow.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map(({ icon: Icon, title, desc }) => (
              <div
                key={title}
                className="group bg-white border border-neutral-100 hover:border-primary-200 rounded-2xl p-6 shadow-card hover:shadow-md transition-all"
              >
                <div className="w-11 h-11 bg-primary-50 group-hover:bg-primary-100 rounded-xl flex items-center justify-center mb-4 transition-colors">
                  <Icon className="w-5 h-5 text-primary-600" />
                </div>
                <h3 className="text-base font-semibold text-neutral-900 mb-2">{title}</h3>
                <p className="text-sm text-neutral-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Audience split CTA */}
      <section className="py-16 bg-neutral-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-6">
            <Link
              to="/for-providers"
              className="group relative overflow-hidden bg-gradient-to-br from-primary-700 to-primary-900 rounded-2xl p-8 text-white hover:shadow-xl transition-shadow"
            >
              <div className="absolute -right-8 -top-8 w-32 h-32 bg-white/5 rounded-full" />
              <div className="absolute -right-4 -bottom-4 w-24 h-24 bg-white/5 rounded-full" />
              <p className="text-xs font-semibold uppercase tracking-widest text-primary-300 mb-2">For Providers</p>
              <h3 className="text-2xl font-bold mb-3">Run your practice, not your paperwork</h3>
              <p className="text-primary-200 text-sm leading-relaxed mb-5">
                Billing automation, HIPAA-compliant notes, insurance claims, and client management — all in one place.
              </p>
              <span className="inline-flex items-center gap-2 text-sm font-semibold text-white group-hover:gap-3 transition-all">
                Learn more <ArrowRightIcon className="w-4 h-4" />
              </span>
            </Link>

            <Link
              to="/for-clients"
              className="group relative overflow-hidden bg-gradient-to-br from-cyan-600 to-primary-700 rounded-2xl p-8 text-white hover:shadow-xl transition-shadow"
            >
              <div className="absolute -right-8 -top-8 w-32 h-32 bg-white/5 rounded-full" />
              <div className="absolute -right-4 -bottom-4 w-24 h-24 bg-white/5 rounded-full" />
              <p className="text-xs font-semibold uppercase tracking-widest text-cyan-200 mb-2">For Clients</p>
              <h3 className="text-2xl font-bold mb-3">Your care, always within reach</h3>
              <p className="text-cyan-100 text-sm leading-relaxed mb-5">
                Book sessions, message your provider, view invoices, and manage your care — all from your phone or computer.
              </p>
              <span className="inline-flex items-center gap-2 text-sm font-semibold text-white group-hover:gap-3 transition-all">
                Learn more <ArrowRightIcon className="w-4 h-4" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* Why we built Sage */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">

            {/* Story */}
            <div>
              <div className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600 uppercase tracking-widest mb-4">
                <HeartIcon className="w-4 h-4" />
                Why we built this
              </div>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-neutral-900 tracking-tight mb-6">
                Built because therapists deserve better tools
              </h2>
              <p className="text-neutral-600 leading-relaxed mb-4">
                My wife is a therapist. She&apos;s incredible at her work — and she spends hours every week
                wrestling with her practice management software instead of focusing on her clients.
              </p>
              <p className="text-neutral-600 leading-relaxed mb-4">
                The tools therapists use today weren&apos;t designed with clinicians in mind. Notes are buried
                in menus. Insurance claims are a maze. Scheduling is clunky. Everything feels like it was
                built for a billing department, not a person who went to school to help people.
              </p>
              <p className="text-neutral-600 leading-relaxed">
                Sage is our answer to that. Intuitive enough that you don&apos;t need a training session to
                get started. Powerful enough to run a full practice. Built so therapists can spend their
                energy on therapy — not on paperwork, payments, or claims.
              </p>
            </div>

            {/* Early access card */}
            <div className="bg-primary-50 border border-primary-100 rounded-2xl p-8">
              <p className="text-xs font-semibold text-primary-600 uppercase tracking-widest mb-2">Early access</p>
              <h3 className="text-2xl font-bold text-neutral-900 mb-2">Be among our founding practices</h3>
              <p className="text-sm text-neutral-500 mb-6">
                We&apos;re in early access and looking for practices ready to be part of something new.
              </p>
              <ul className="space-y-3 mb-8">
                {earlyAccessBenefits.map((benefit) => (
                  <li key={benefit} className="flex items-start gap-3 text-sm text-neutral-700">
                    <CheckCircleIcon className="w-5 h-5 text-primary-500 flex-shrink-0 mt-0.5" />
                    {benefit}
                  </li>
                ))}
              </ul>
              <Link
                to="/register"
                className="inline-flex items-center justify-center gap-2 w-full bg-primary-600 hover:bg-primary-700 text-white font-semibold px-6 py-3.5 rounded-xl transition-colors"
              >
                Start free trial
                <ArrowRightIcon className="w-4 h-4" />
              </Link>
              <p className="text-xs text-neutral-400 text-center mt-3">No credit card required</p>
            </div>

          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-primary-600 to-primary-900 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-4">
            Ready to transform your practice?
          </h2>
          <p className="text-lg text-white/80 mb-8">
            Be one of the first practices to run on Sage — and help us build the practice management
            tool therapists actually want to use.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/register"
              className="inline-flex items-center gap-2 bg-white text-primary-700 font-bold px-7 py-3.5 rounded-xl hover:bg-primary-50 transition-colors shadow-lg"
            >
              Start your free trial
              <ArrowRightIcon className="w-4 h-4" />
            </Link>
            <Link
              to="/login"
              className="text-sm font-medium text-primary-200 hover:text-white transition-colors"
            >
              Already have an account? Log in →
            </Link>
          </div>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-5 text-sm text-primary-300">
            {['No credit card required', 'Free 30-day trial', 'HIPAA compliant', 'Cancel anytime'].map((item) => (
              <span key={item} className="flex items-center gap-1.5">
                <CheckCircleIcon className="w-4 h-4 text-primary-400" />
                {item}
              </span>
            ))}
          </div>
        </div>
      </section>
    </LandingLayout>
  );
}
